import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import { ICONS } from '@shared/components/icons/icon.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import useProfileStore from '@store/profile-store/profile.store.ts';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ProductsModel } from '@models/products.model.ts';
import { productsColumns } from '@modules/products/components/products-table/components/columns/products-columns.tsx';
import ProductsDialogContent from '@modules/products/components/products-dialog/products-dialog-content.tsx';
import useProductsStore from '@modules/products/store/products.store.ts';
import { getProducts } from '@api/products-api.ts';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import ViewControls from '@components/view-controls/view-controls.tsx';
import { RoutePaths } from '@utils/route.utils.ts';
import { SortOrder, TriggerTypeEnum } from '@shared/models/table.model';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import useProductCategoriesStore from '@modules/product-categories/store/product-categories.store.ts';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';

const ProductsTable: React.FC = () => {
  const {
    createProduct,
    editProduct,
    deleteProduct,
    getInventoryCategories,
    togglePublish,
    isLoading
  } = useProductsStore();

  const { getDepartmentUnit } = useProductCategoriesStore();
  const { selectedCurrency } = useProfileStore();
  const { resetAllSelectedMedia } = useMediaLibraryStore();
  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [updateProduct, setUpdateProduct] = useState<ProductsModel | null>(
    null
  );
  const [language, setLanguage] = useState(LanguageEnum.English);
  const [searchParams, setSearchParams] = useSearchParams();

  const isTabletScreen = useMediaQuery('(max-width: 1715px)');
  const location = useLocation();
  const isProductsPage =
    location.pathname === RoutePaths.Core.Products.absolutePath;

  useEffect(() => {
    const fetchDataAndOpenDialog = async () => {
      await Promise.all([getInventoryCategories(), getDepartmentUnit()]);

      const dialog = searchParams.get('dialog');
      if (dialog === 'open') {
        handleOpen();
      }
    };

    fetchDataAndOpenDialog().then();
  }, []);

  const handleEdit = (row: ProductsModel) => {
    setUpdateProduct(row);
    setOpenDialog(true);
  };

  const handleDelete = (row: ProductsModel) => {
    setUpdateProduct(row);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    deleteProduct({
      id: updateProduct?.id,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleClose = () => {
    resetAllSelectedMedia();
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setUpdateProduct(null);
  };

  const handleOpen = () => setOpenDialog(true);

  const handleFormSave = (data: any) => {
    if (updateProduct?.id) {
      editProduct({
        data,
        id: updateProduct?.id,
        state: tableStore.getState(),
        handleClose
      });
    } else {
      createProduct({
        data,
        state: tableStore.getState(),
        handleClose
      });
    }
  };

  const handleAction = (action: { type: string; value?: string }) => {
    if (action.type === ViewControlsEnum.Language) {
      setLanguage(action.value as LanguageEnum);
    }
  };

  const handlePublish = (row: ProductsModel) => {
    togglePublish({
      id: row?.id,
      state: tableStore.getState(),
      handleClose
    });
  };

  const columns = productsColumns(
    handlePublish,
    handleEdit,
    handleDelete,
    selectedCurrency,
    language,
    t
  );

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls onAction={handleAction} />

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={handleOpen}
        >
          {t('add')}
        </Button>
      </div>

      <div
        className={
          isProductsPage
            ? TABLE_HEIGHT.WITHOUT_TABS
            : TABLE_HEIGHT.WITH_TABS_AND_ACTIONS
        }
      >
        <DynamicTable
          onTriggerAction={handleTableTrigger}
          tableStore={tableStore}
          apiFunction={getProducts}
          columns={columns}
          language={language}
          tableKey="products-table"
          initialState={{
            sort: ['createdAt', SortOrder.Descending]
          }}
        />
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          size={isTabletScreen ? 'md' : 'lg'}
          title={t('products_dialog_title')}
          content={
            <ProductsDialogContent
              updateProductData={updateProduct}
              onSave={handleFormSave}
            />
          }
          onClose={handleClose}
        />

        <div className="w-full h-full">
          <DeleteDialog
            onCancel={handleClose}
            onDelete={handleDeleteConfirm}
            open={showDeleteDialog}
            isLoading={isLoading()}
            title={t('delete_popup_title')}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductsTable;
